import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactHotkeys from 'react-hot-keys';
import styled, { css } from 'styled-components';

import { CrossSvg } from '../assets/icons';
import { DragSvg } from '../assets/icons/DragSvg';
import theme from '../assets/theme';
import { zIndexes } from '../utils/zIndexes';
import { IconWrapper } from './_atoms/IconWrapper';
import {
  clampReadAloudModalWidth,
  readStoredReadAloudModalWidth,
  writeStoredReadAloudModalWidth,
} from './ReadAloud/readAloudModalWidthStorage';

/**
 * Width of the passage scrollbar, owned here because the top/bottom fades have
 * to stop short of it — a gradient painted over the bar reads as the bar being
 * cut off. Consumed by the scrolling child, which sizes its own scrollbar to
 * match.
 */
export const READ_ALOUD_SCROLLBAR_WIDTH = 10;

/**
 * Height of the fade at the top and bottom of the card. The scrolling child
 * carries the same value as vertical padding, so at rest the fade covers only
 * blank space and the first and last lines stay crisp.
 */
export const READ_ALOUD_FADE_HEIGHT = 32;

const Portal = ({ children }: { children: React.ReactNode }) => {
  return typeof document === 'object' ? createPortal(children, document.body) : null;
};

const scaleFromCenter = {
  hidden: {
    opacity: 0,
    transform: 'scale(0)',
  },
  visible: {
    opacity: 1,
    transform: 'scale(1)',
    transition: {
      delay: 0.1,
      duration: 0.2,
    },
  },
  exit: {
    opacity: 0,
    transform: 'scale(0)',
  },
};

const maskOpacity = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.2,
    },
  },
  exit: {
    opacity: 0,
  },
};

export const ReadAloudModal = ({
  onClose,
  margin = 80,
  children,
}: {
  /** Gap between the visible edge of the card and the viewport, top and bottom. */
  margin?: number;
  onClose: () => void;
  children: React.ReactNode;
}) => {
  const [width, setWidth] = useState(readStoredReadAloudModalWidth);
  const [isResizing, setIsResizing] = useState(false);
  // Pointerup can land in the same batch as the last pointermove, so read the
  // width to persist off a ref rather than off possibly-uncommitted state.
  const widthRef = useRef(width);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  const handleResizeStart = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    // Stops the browser starting a text selection / touch scroll instead.
    e.preventDefault();
    try {
      // Capture keeps the move events coming once the pointer outruns the grip,
      // which it does at the clamped min and max widths. Not worth failing the
      // whole drag over though — it throws when the pointer is already gone (a
      // fast tap), and without it a drag still tracks while over the grip.
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {}
    dragRef.current = { startX: e.clientX, startWidth: widthRef.current };
    setIsResizing(true);
  }, []);

  const handleResizeMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    // The card is centred, so its right edge only travels half of any width
    // change — double the delta to keep the grip under the finger/cursor.
    const next = clampReadAloudModalWidth(
      drag.startWidth + (e.clientX - drag.startX) * 2,
    );
    widthRef.current = next;
    setWidth(next);
  }, []);

  const handleResizeEnd = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    setIsResizing(false);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    // Only persist an actual resize. A bare tap on the grip would otherwise
    // write back the width currently on screen — which, on a laptop opening a
    // width dragged out on a larger monitor, is the clamped-down value, quietly
    // destroying the wider setting.
    if (widthRef.current !== drag.startWidth) {
      writeStoredReadAloudModalWidth(widthRef.current);
    }
  }, []);

  return (
    <Portal>
      <ReactHotkeys
        keyName={'Escape'}
        onKeyDown={(_, event) => {
          event.preventDefault();
          onClose();
        }}
      />
      <AnimatePresence>
        <Mask
          onClick={onClose}
          variants={maskOpacity}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <ResetHeightWidthContainer>
            <MaxSizeContainer $margin={margin} $width={width}>
              <ResetHeightWidthContainer>
                <ContentContainer
                  onClick={e => {
                    // Need this or else the mask onClick will be triggered
                    e.stopPropagation();
                  }}
                  $isResizing={isResizing}
                  variants={scaleFromCenter}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <CloseContainer onClick={onClose}>
                    <IconWrapper>
                      <CrossSvg />
                    </IconWrapper>
                  </CloseContainer>
                  {children}
                  <ResizeHandle
                    role="separator"
                    aria-orientation="vertical"
                    aria-label="Drag to resize the passage"
                    title="Drag to resize"
                    $isResizing={isResizing}
                    onPointerDown={handleResizeStart}
                    onPointerMove={handleResizeMove}
                    onPointerUp={handleResizeEnd}
                    onPointerCancel={handleResizeEnd}
                  >
                    <ResizeHandleBar>
                      <IconWrapper
                        color={theme.colors.white}
                        height="14px"
                        width="14px"
                        cursor="ew-resize"
                      >
                        <DragSvg />
                      </IconWrapper>
                    </ResizeHandleBar>
                  </ResizeHandle>
                </ContentContainer>
              </ResetHeightWidthContainer>
            </MaxSizeContainer>
          </ResetHeightWidthContainer>
        </Mask>
      </AnimatePresence>
    </Portal>
  );
};

const ResetHeightWidthContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`;

const CloseContainer = styled.div`
  position: absolute;
  top: 0;
  right: -55px;
  z-index: ${zIndexes('FULL_SIZE_MODAL')};
  height: 50px;
  width: 50px;
  display: grid;
  justify-content: center;
  align-items: center;
  background: rgba(17, 23, 29, 0.6);
  color: white;
  border-radius: 100px;
  box-shadow: 0px 0px 0px 1px rgba(64, 87, 109, 0.04),
    0px 6px 20px -4px rgba(64, 87, 109, 0.3);

  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);

  &:hover {
    background: black;
  }
`;

/**
 * The visible white card. The children own their own padding so that a
 * scrolling child can put its scrollbar flush against this card's edge — a
 * padding here would push it inwards and leave it floating in white space.
 */
const ContentContainer = styled(motion.div)<{ $isResizing: boolean }>`
  // Reset styles
  position: relative;
  display: block;
  box-sizing: border-box;

  width: 100%;
  height: 100%;

  // Styles
  background: ${theme.colors.white};
  border-radius: 10px;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);

  ${({ $isResizing }) =>
    $isResizing &&
    css`
      user-select: none;
    `}

  /* Fade the passage out against the card at both ends. Painted here rather
     than on the scroller because an absolutely positioned child of a scroll
     container scrolls with its content — these have to stay put. Held clear of
     the scrollbar column on the right, and pointer-transparent so the wheel
     still reaches the text underneath. */
  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: ${READ_ALOUD_SCROLLBAR_WIDTH}px;
    height: ${READ_ALOUD_FADE_HEIGHT}px;
    pointer-events: none;
    z-index: 1;
  }

  &::before {
    top: 0;
    border-radius: 10px 0 0 0;
    background: linear-gradient(
      to bottom,
      ${theme.colors.white} 0%,
      rgba(255, 255, 255, 0) 100%
    );
  }

  &::after {
    bottom: 0;
    border-radius: 0 0 0 10px;
    background: linear-gradient(
      to top,
      ${theme.colors.white} 0%,
      rgba(255, 255, 255, 0) 100%
    );
  }
`;

/** Same dark translucent chrome as the close button and the font-size panel. */
const ResizeHandleBar = styled.div`
  width: 18px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;

  background: rgba(17, 23, 29, 0.6);
  border-radius: 10px;
  box-shadow: 0px 0px 0px 1px rgba(64, 87, 109, 0.04),
    0px 6px 20px -4px rgba(64, 87, 109, 0.3);

  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
`;

/**
 * Hit area, deliberately larger than the bar it wraps and offset to sit wholly
 * outside the card — overlapping inwards would put it on top of the scrollbar.
 * `touch-action: none` is what makes a finger drag resize rather than scroll.
 */
const ResizeHandle = styled.div<{ $isResizing: boolean }>`
  position: absolute;
  top: 50%;
  right: -28px;
  transform: translateY(-50%);
  z-index: ${zIndexes('FULL_SIZE_MODAL')};

  width: 28px;
  height: 110px;
  display: flex;
  align-items: center;
  justify-content: center;

  cursor: ew-resize;
  touch-action: none;

  ${({ $isResizing }) =>
    $isResizing &&
    css`
      ${ResizeHandleBar} {
        background: black;
      }
    `}

  &:hover ${ResizeHandleBar} {
    background: black;
  }
`;

const MaxSizeContainer = styled.div<{ $margin: number; $width: number }>`
  position: absolute;

  ${({ $margin, $width }) => css`
    height: calc(100% - ${$margin * 2}px);
    width: ${$width}px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  `}
`;

const Mask = styled(motion.div)`
  z-index: ${zIndexes('FULL_SIZE_MODAL')};
  position: fixed; // Prevent scrolling the modal mask
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;

  background: rgba(0, 0, 0, 0.6);
`;
